import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";

/**
 * Hook que vigila nuevos servicios y dispara alertas sonoras/visuales.
 *
 * Para internos (cabina, dispatch, superadmin, dirección, comercial): detecta
 *   servicios nuevos creados (status "nuevo" o "asignado"), aún sin tripulación.
 * Para tripulación (ambulancia, paramedico, operador, medico, enfermera): detecta
 *   servicios que les fueron asignados (status "tripulacion_notificada"
 *   o "unidad_asignada" sobre su unit_id).
 *
 * Devuelve:
 *   unreadCount  : número de alertas no vistas
 *   alerts       : lista de últimas alertas (folio, cliente, ts)
 *   markAllRead  : función para limpiar el contador
 *   enableAudio  : habilita audio (debe llamarse tras user gesture por política de browsers)
 *   audioEnabled : si el usuario ya activó audio en esta sesión
 */
export function useServiceAlerts(user) {
    const [alerts, setAlerts] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [audioEnabled, setAudioEnabled] = useState(false);
    const lastSeenIdsRef = useRef(new Set());
    const audioCtxRef = useRef(null);
    const initializedRef = useRef(false);

    const isCrew = ["ambulancia", "paramedico", "operador", "medico", "enfermera"].includes(user?.role || "");
    const isInternal = ["superadmin", "direccion", "cabina", "coordinador", "gerente", "comercial", "dispatch"].includes(user?.role || "");

    // Reproduce un BIP doble usando WebAudio (sin requerir archivos mp3 en el bundle)
    const playBeep = useCallback(() => {
        try {
            const ctx = audioCtxRef.current;
            if (!ctx) return;
            const beepAt = (start, freq, dur = 0.18, gainVal = 0.25) => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.type = "sine";
                osc.frequency.value = freq;
                gain.gain.value = gainVal;
                osc.connect(gain);
                gain.connect(ctx.destination);
                osc.start(ctx.currentTime + start);
                osc.stop(ctx.currentTime + start + dur);
            };
            beepAt(0, 880);     // tono medio-alto
            beepAt(0.22, 1100); // segundo más agudo
            beepAt(0.50, 1320); // tercer tono final (más urgente)
        } catch (e) {
            console.warn("Audio beep failed:", e?.message);
        }
    }, []);

    const enableAudio = useCallback(() => {
        if (audioEnabled) return;
        try {
            const AudioCtx = window.AudioContext || window.webkitAudioContext;
            if (!AudioCtx) {
                console.warn("WebAudio no soportado");
                return;
            }
            audioCtxRef.current = new AudioCtx();
            // Pedir permiso para notificaciones del navegador (push tipo OS)
            if ("Notification" in window && Notification.permission === "default") {
                Notification.requestPermission().catch(() => {});
            }
            // Beep de prueba para confirmar al usuario que está activo
            const ctx = audioCtxRef.current;
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = "sine";
            osc.frequency.value = 660;
            gain.gain.value = 0.1;
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.start(ctx.currentTime);
            osc.stop(ctx.currentTime + 0.12);
            setAudioEnabled(true);
            try { localStorage.setItem("findmed_audio_alerts", "1"); } catch { /* ignore */ }
        } catch (e) {
            console.warn("No se pudo activar audio:", e?.message);
        }
    }, [audioEnabled]);

    const markAllRead = useCallback(() => setUnreadCount(0), []);

    // Auto-restore audio preference (mostrar prompt en CTA pero sin volver a tocar)
    useEffect(() => {
        try {
            const pref = localStorage.getItem("findmed_audio_alerts");
            if (pref === "1") {
                // No podemos crear AudioContext sin user gesture; solo recordamos preferencia
                // El próximo click del usuario activará el audio real.
                // Pero al menos mostramos el toggle como "ON" en UI.
                setAudioEnabled(false); // se reactiva con primer enableAudio
            }
        } catch { /* ignore */ }
    }, []);

    // Polling principal
    useEffect(() => {
        if (!user || (!isCrew && !isInternal)) return;

        const filterFn = (s) => {
            if (isInternal) {
                // Servicios recién creados que aún no tienen unidad asignada
                return ["nuevo", "asignado"].includes(s.status);
            }
            if (isCrew) {
                // Asignaciones nuevas para esta tripulación / unidad
                if (user.role === "ambulancia") return s.unit_id === user.unit_id && ["tripulacion_notificada", "unidad_asignada"].includes(s.status);
                return (s.paramedic_id === user.id || s.driver_id === user.id) && ["tripulacion_notificada", "unidad_asignada"].includes(s.status);
            }
            return false;
        };

        const fetchAndDiff = async () => {
            try {
                const { data } = await api.get("/services?limit=50");
                if (!Array.isArray(data)) return;
                const matched = data.filter(filterFn);
                const knownIds = lastSeenIdsRef.current;
                const newOnes = matched.filter((s) => !knownIds.has(s.id));

                if (!initializedRef.current) {
                    // Primera carga: solo registra, no alerta
                    matched.forEach((s) => knownIds.add(s.id));
                    initializedRef.current = true;
                    return;
                }

                if (newOnes.length > 0) {
                    newOnes.forEach((s) => knownIds.add(s.id));
                    setUnreadCount((c) => c + newOnes.length);
                    setAlerts((prev) => [
                        ...newOnes.map((s) => ({
                            id: s.id,
                            folio: s.folio,
                            client_name: s.client_name || s.patient_name || "Sin cliente",
                            status: s.status,
                            at: new Date().toISOString(),
                        })),
                        ...prev,
                    ].slice(0, 20));

                    // Audio
                    if (audioCtxRef.current) playBeep();

                    // Browser notification (push estilo OS)
                    if ("Notification" in window && Notification.permission === "granted") {
                        const title = isInternal ? "🆘 Nueva solicitud" : "🚑 Nuevo servicio asignado";
                        const body = newOnes
                            .map((s) => `${s.folio} · ${s.client_name || s.patient_name || ""}`)
                            .slice(0, 3)
                            .join("\n");
                        try {
                            const n = new Notification(title, {
                                body,
                                tag: "findmed-service",
                                requireInteraction: false,
                            });
                            setTimeout(() => n.close(), 8000);
                        } catch { /* ignore */ }
                    }
                }
            } catch (e) {
                console.warn("alerts poll error:", e?.message);
            }
        };

        // Primera carga inmediata + intervalo de 15s
        fetchAndDiff();
        const t = setInterval(fetchAndDiff, 15_000);
        return () => clearInterval(t);
    }, [user, isCrew, isInternal, playBeep]);

    return { alerts, unreadCount, markAllRead, enableAudio, audioEnabled };
}
